/**
 * Dedicated Strong's numbers section in a rich chat response.
 * Shows each Strong's number with native text and short definition.
 * Click opens the full StrongsPanel bottom drawer.
 */

import { useCallback } from 'react';
import type { StrongsRef, NotableStrongsRef } from '@skjv/common';
import { bibleState } from '@/state/bible-state';

interface StrongsSectionProps {
  strongsRefs: StrongsRef[];
  title?: string;
}

function isNotable(ref: StrongsRef): ref is NotableStrongsRef {
  return 'word' in ref && 'frequency' in ref;
}

export function StrongsSection({ strongsRefs, title }: StrongsSectionProps) {
  if (strongsRefs.length === 0) return null;

  return (
    <div className="rich-message-section">
      <h4 className="rich-message-section-title">{title ?? "Strong\u2019s Numbers"}</h4>
      <div className="strongs-section-list">
        {strongsRefs.map((ref) => (
          <StrongsSectionItem key={ref.number} strongsRef={ref} />
        ))}
      </div>
    </div>
  );
}

function StrongsSectionItem({ strongsRef }: { strongsRef: StrongsRef }) {
  const handleClick = useCallback(() => {
    bibleState.showStrongsDefinition(strongsRef.number, bibleState.currentLocation);
  }, [strongsRef.number]);

  return (
    <button className="strongs-section-item" onClick={handleClick}>
      <span className="strongs-section-number strongs-link">{strongsRef.number}</span>
      {isNotable(strongsRef) && (
        <span className="strongs-section-word">&ldquo;{strongsRef.word}&rdquo;</span>
      )}
      {strongsRef.native && (
        <span className="strongs-section-native">{strongsRef.native}</span>
      )}
      {strongsRef.shortDefinition && (
        <span className="strongs-section-def">{strongsRef.shortDefinition}</span>
      )}
    </button>
  );
}
